import React from 'react';
import { formatCurrency, getStatusColor } from '../../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Receipt, CreditCard, Eye, FileText } from 'lucide-react';

export const BillsTable = ({
  bills, loading, statusFilter, setStatusFilter, generatingInvoice, openPaymentDialog, handleGenerateInvoice, viewBill
}) => (
    <Card>
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
            <Receipt className="w-5 h-5 text-red-500" />
            All Bills
          </CardTitle>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading bills...</div>
        ) : bills.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No bills found.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bill ID</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Paid</TableHead>
                <TableHead>Balance</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bills.map((bill) => (
                <TableRow key={bill.id} data-testid={`bill-row-${bill.id}`}>
                  <TableCell className="font-mono text-sm">{bill.bill_id}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{bill.patient?.full_name}</p>
                      <p className="text-xs text-slate-500">{bill.patient?.patient_id}</p>
                    </div>
                  </TableCell>
                  <TableCell>{bill.items?.length || 0} items</TableCell>
                  <TableCell className="font-semibold">{formatCurrency(bill.total_amount)}</TableCell>
                  <TableCell className="text-emerald-600">{formatCurrency(bill.paid_amount || 0)}</TableCell>
                  <TableCell className="text-red-500">{formatCurrency(bill.total_amount - (bill.paid_amount || 0))}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(bill.status)}>{bill.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => viewBill(bill)} data-testid={`view-${bill.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {bill.status !== 'paid' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openPaymentDialog(bill)}
                          data-testid={`pay-${bill.id}`}
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          Pay
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleGenerateInvoice(bill)}
                        disabled={generatingInvoice}
                        title="Generate Invoice"
                        data-testid={`generate-invoice-${bill.id}`}
                      >
                        <FileText className="w-4 h-4 text-blue-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
);
